import { forwardRef, type InputHTMLAttributes, useId } from 'react';
import { cn } from '../cn.js';

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  /** Shown below the input. Supersedes helper when both are set. */
  error?: string;
  helper?: string;
}

/**
 * Labelled text input. 44px target, tokens-aligned. Forwards ref so it can be
 * registered by react-hook-form.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, helper, className, ...props },
  ref,
) {
  const id = useId();
  const describedBy = error ? `${id}-error` : helper ? `${id}-helper` : undefined;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
        {label}
      </label>
      <input
        id={id}
        ref={ref}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          'min-h-touch rounded-md border border-hairline bg-surface-1 px-3 text-sm text-ink-0 outline-none transition-colors placeholder:text-ink-3 focus-visible:border-aurora/60 focus-visible:ring-2 focus-visible:ring-aurora/40 disabled:cursor-not-allowed disabled:opacity-60',
          className,
        )}
        {...props}
      />
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-xs text-rose">
          {error}
        </p>
      ) : helper ? (
        <p id={`${id}-helper`} className="text-xs text-ink-3">
          {helper}
        </p>
      ) : null}
    </div>
  );
});
